import React from 'react';
import { Link } from 'react-router-dom';
import styled, { keyframes } from 'styled-components';

// Animations
const fadeIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const slideInLeft = keyframes`
  from {
    opacity: 0;
    transform: translateX(-30px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
`;

const slideInRight = keyframes`
  from {
    opacity: 0;
    transform: translateX(30px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
`;

const pulse = keyframes`
  0% { transform: scale(1); }
  50% { transform: scale(1.05); }
  100% { transform: scale(1); }
`;

const float = keyframes`
  0% { transform: translateY(0px); }
  50% { transform: translateY(-10px); }
  100% { transform: translateY(0px); }
`;

// Styled Components
const PageContainer = styled.div`
  font-family: 'Inter', system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
  background: linear-gradient(135deg, #e0f2fe 0%, #bae6fd 100%);
  overflow-x: hidden;
`;

// Hero Section
const HeroSection = styled.div`
  position: relative;
  height: 70vh;
  min-height: 500px;
  background-image: url('https://images.unsplash.com/photo-1579684385127-1ef15d508118?w=1600&h=800&fit=crop');
  background-size: cover;
  background-position: center;
  background-attachment: fixed;
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(135deg, rgba(2, 84, 117, 0.85) 0%, rgba(3, 105, 161, 0.75) 100%);
  }
  
  @media (max-width: 768px) {
    height: 50vh;
    min-height: 400px;
    background-attachment: scroll;
  }
`;

const HeroContent = styled.div`
  position: relative;
  z-index: 2;
  color: white;
  max-width: 800px;
  padding: 0 20px;
  animation: ${fadeIn} 0.8s ease-out;
`;

const HeroTitle = styled.h1`
  font-size: 3rem;
  font-weight: 800;
  margin-bottom: 1rem;
  text-shadow: 2px 2px 4px rgba(0,0,0,0.3);
  
  @media (min-width: 768px) {
    font-size: 4rem;
  }
  
  @media (min-width: 1024px) {
    font-size: 4.5rem;
  }
`;

const HeroSubtitle = styled.p`
  font-size: 1.2rem;
  margin-bottom: 2rem;
  opacity: 0.95;
  
  @media (min-width: 768px) {
    font-size: 1.4rem;
  }
`;

const HeroButton = styled.button`
  background: white;
  color: #0284c7;
  border: none;
  padding: 12px 32px;
  font-size: 1.1rem;
  font-weight: 600;
  border-radius: 50px;
  cursor: pointer;
  transition: transform 0.3s ease, box-shadow 0.3s ease;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 10px 25px rgba(0,0,0,0.2);
    animation: ${pulse} 0.5s ease;
  }
`;

// Content Container
const ContentContainer = styled.div`
  max-width: 1280px;
  margin: 0 auto;
  padding: 3rem 1.5rem;
  
  @media (min-width: 768px) {
    padding: 4rem 2rem;
  }
`;

// Section Styles
const Section = styled.section`
  margin-bottom: 4rem;
  animation: ${fadeIn} 0.6s ease-out;
  animation-delay: ${props => props.delay || '0s'};
  animation-fill-mode: backwards;
`;

const SectionTitle = styled.h2`
  font-size: 1.8rem;
  font-weight: 700;
  color: #0c4a6e;
  margin-bottom: 1.5rem;
  position: relative;
  display: inline-block;
  
  &::after {
    content: '';
    position: absolute;
    bottom: -10px;
    left: 0;
    width: 60px;
    height: 4px;
    background: linear-gradient(135deg, #0284c7, #0c4a6e);
    border-radius: 2px;
  }
  
  @media (min-width: 768px) {
    font-size: 2.2rem;
  }
`;

const SectionSubtitle = styled.p`
  font-size: 1.1rem;
  color: #475569;
  margin-bottom: 2rem;
  line-height: 1.6;
`;

// Grid Layouts
const TwoColumnGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 2rem;
  
  @media (min-width: 768px) {
    grid-template-columns: repeat(2, 1fr);
    gap: 3rem;
  }
`;

const ThreeColumnGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 2rem;
  
  @media (min-width: 768px) {
    grid-template-columns: repeat(2, 1fr);
  }
  
  @media (min-width: 1024px) {
    grid-template-columns: repeat(3, 1fr);
  }
`;

const FourColumnGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 1rem;
  
  @media (min-width: 768px) {
    grid-template-columns: repeat(4, 1fr);
    gap: 1.5rem;
  }
`;

// Card Components
const InfoCard = styled.div`
  background: white;
  border-radius: 20px;
  padding: 1.5rem;
  box-shadow: 0 10px 25px rgba(0,0,0,0.1);
  transition: transform 0.3s ease, box-shadow 0.3s ease;
  animation: ${slideInLeft} 0.6s ease-out;
  
  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 20px 35px rgba(2, 132, 199, 0.15);
  }
`;

const InfoCardTitle = styled.h3`
  font-size: 1.3rem;
  font-weight: 700;
  color: #0c4a6e;
  margin-bottom: 1rem;
  display: flex;
  align-items: center;
  gap: 10px;
`;

const InfoCardText = styled.p`
  color: #475569;
  line-height: 1.6;
  margin-bottom: 1rem;
`;

const ListItem = styled.li`
  color: #475569;
  margin-bottom: 0.75rem;
  line-height: 1.5;
  padding-left: 1.5rem;
  position: relative;
  
  &::before {
    content: '💙';
    position: absolute;
    left: 0;
    color: #0284c7;
    font-size: 0.9rem;
  }
`;

// Blood Pressure Display
const BPCard = styled.div`
  background: linear-gradient(135deg, #0c4a6e, #0284c7);
  border-radius: 20px;
  padding: 1.5rem;
  text-align: center;
  color: white;
  animation: ${float} 3s ease-in-out infinite;
`;

const BPNumbers = styled.div`
  display: flex;
  justify-content: center;
  align-items: baseline;
  gap: 0.5rem;
  margin: 1rem 0;
`;

const BPSystolic = styled.span`
  font-size: 3rem;
  font-weight: 800;
  
  @media (min-width: 768px) {
    font-size: 4rem;
  }
`;

const BPDivider = styled.span`
  font-size: 3rem;
  font-weight: 800;
  
  @media (min-width: 768px) {
    font-size: 4rem;
  }
`;

const BPDiastolic = styled.span`
  font-size: 3rem;
  font-weight: 800;
  
  @media (min-width: 768px) {
    font-size: 4rem;
  }
`;

const BPUnit = styled.span`
  font-size: 1rem;
  opacity: 0.9;
`;

const BPCategory = styled.p`
  font-size: 1.2rem;
  margin-top: 0.5rem;
  font-weight: 600;
`;

// Warning Box
const WarningBox = styled.div`
  background: linear-gradient(135deg, #fef3c7, #fde68a);
  border-left: 4px solid #f59e0b;
  padding: 1.5rem;
  border-radius: 15px;
  margin: 1.5rem 0;
`;

const DangerBox = styled.div`
  background: linear-gradient(135deg, #fee2e2, #fecaca);
  border-left: 4px solid #dc2626;
  padding: 1.5rem;
  border-radius: 15px;
  margin: 1.5rem 0;
`;

const SuccessBox = styled.div`
  background: linear-gradient(135deg, #dcfce7, #bbf7d0);
  border-left: 4px solid #22c55e;
  padding: 1.5rem;
  border-radius: 15px;
  margin: 1.5rem 0;
`;

const WarningTitle = styled.h4`
  color: ${props => props.color || '#92400e'};
  font-weight: 700;
  margin-bottom: 0.5rem;
  font-size: 1.1rem;
`;

const RiskBadge = styled.span`
  background: ${props => props.color || '#0284c7'};
  color: white;
  padding: 4px 12px;
  border-radius: 20px;
  font-size: 0.8rem;
  font-weight: 600;
  display: inline-block;
  margin-right: 0.5rem;
`;

// Doctor Section
const DoctorSection = styled.div`
  background: linear-gradient(135deg, #0c4a6e 0%, #0284c7 100%);
  border-radius: 30px;
  padding: 2rem;
  color: white;
  margin-top: 3rem;
  
  @media (min-width: 768px) {
    padding: 3rem;
  }
`;

const DoctorGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 2rem;
  align-items: center;
  
  @media (min-width: 768px) {
    grid-template-columns: 280px 1fr;
  }
`;

const DoctorImage = styled.img`
  width: 100%;
  max-width: 250px;
  border-radius: 20px;
  box-shadow: 0 20px 35px rgba(0,0,0,0.3);
  margin: 0 auto;
  display: block;
  
  @media (min-width: 768px) {
    max-width: 100%;
  }
`;

const DoctorContent = styled.div``;

const DoctorName = styled.h3`
  font-size: 1.8rem;
  font-weight: 700;
  margin-bottom: 0.5rem;
  color: #bae6fd;
`;

const DoctorTitle = styled.p`
  font-size: 1rem;
  color: #e0f2fe;
  margin-bottom: 1rem;
`;

const DoctorDescription = styled.p`
  line-height: 1.7;
  margin-bottom: 1rem;
  color: #f0f9ff;
`;

const CTAButton = styled.button`
  background: white;
  color: #0284c7;
  border: none;
  padding: 12px 28px;
  font-size: 1rem;
  font-weight: 600;
  border-radius: 50px;
  cursor: pointer;
  margin-top: 1rem;
  transition: all 0.3s ease;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 10px 25px rgba(0,0,0,0.2);
  }
`;

// Image Gallery
const ImageGallery = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 1rem;
  margin: 2rem 0;
  
  @media (min-width: 768px) {
    grid-template-columns: repeat(4, 1fr);
    gap: 1.5rem;
  }
`;

const GalleryImage = styled.img`
  width: 100%;
  height: 180px;
  object-fit: cover;
  border-radius: 15px;
  transition: transform 0.3s ease;
  cursor: pointer;
  
  &:hover {
    transform: scale(1.05);
  }
  
  @media (min-width: 768px) {
    height: 200px;
  }
`;

// Stats Component
const StatCard = styled.div`
  text-align: center;
  padding: 1.5rem;
  background: white;
  border-radius: 20px;
  box-shadow: 0 5px 15px rgba(0,0,0,0.05);
  transition: transform 0.3s ease;
  
  &:hover {
    transform: translateY(-5px);
  }
`;

const StatNumber = styled.div`
  font-size: 2.5rem;
  font-weight: 800;
  color: #0284c7;
  
  @media (min-width: 768px) {
    font-size: 3rem;
  }
`;

const StatLabel = styled.div`
  color: #64748b;
  font-size: 0.9rem;
  margin-top: 0.5rem;
`;

// Main Component
const BloodPressurePage = () => {
  const handleAppointment = () => {
    alert('Book an appointment with Dr. Raj Kamal for Blood Pressure Management');
  };

  const galleryImages = [
    'https://images.unsplash.com/photo-1579684385127-1ef15d508118?w=400&h=300&fit=crop',
    'https://images.unsplash.com/photo-1505751172876-fa1923c5c528?w=400&h=300&fit=crop',
    'https://images.unsplash.com/photo-1530026405186-ed1f139313f8?w=400&h=300&fit=crop',
    'https://images.unsplash.com/photo-1516549655169-df83a0774514?w=400&h=300&fit=crop',
  ];

  return (
    <PageContainer>
      {/* Hero Section */}
      <HeroSection>
        <HeroContent>
          <HeroTitle>Understanding Blood Pressure</HeroTitle>
          <HeroSubtitle>
            The Silent Killer | Comprehensive hypertension management by Dr. Raj Kamal
          </HeroSubtitle>
          <HeroButton as={Link} to="/book-appointment">Book Consultation</HeroButton>
        </HeroContent>
      </HeroSection>

      <ContentContainer>
        {/* Statistics Section */}
        <Section delay="0.05s">
          <FourColumnGrid>
            <StatCard>
              <StatNumber>1.28B</StatNumber>
              <StatLabel>Adults worldwide have hypertension</StatLabel>
            </StatCard>
            <StatCard>
              <StatNumber>46%</StatNumber>
              <StatLabel>Adults with hypertension are unaware</StatLabel>
            </StatCard>
            <StatCard>
              <StatNumber>2x</StatNumber>
              <StatLabel>Higher risk of heart attack with high BP</StatLabel>
            </StatCard>
            <StatCard>
              <StatNumber>120/80</StatNumber>
              <StatLabel>Optimal blood pressure target</StatLabel>
            </StatCard>
          </FourColumnGrid>
        </Section>

        {/* What is Blood Pressure Section */}
        <Section delay="0.1s">
          <SectionTitle>What is Blood Pressure?</SectionTitle>
          <TwoColumnGrid>
            <InfoCard>
              <InfoCardTitle>
                <span>📊</span> Understanding the Numbers
              </InfoCardTitle>
              <InfoCardText>
                Blood pressure is the force of blood pushing against the walls of your arteries as your heart pumps blood. It's measured using two numbers:
              </InfoCardText>
              <BPCard>
                <BPNumbers>
                  <BPSystolic>120</BPSystolic>
                  <BPDivider>/</BPDivider>
                  <BPDiastolic>80</BPDiastolic>
                  <BPUnit>mmHg</BPUnit>
                </BPNumbers>
                <BPCategory>Normal Blood Pressure</BPCategory>
              </BPCard>
              <InfoCardText style={{ marginTop: '1rem' }}>
                <strong>Systolic (top number):</strong> Pressure when your heart beats<br />
                <strong>Diastolic (bottom number):</strong> Pressure when your heart rests between beats
              </InfoCardText>
            </InfoCard>
            <div>
              <img 
                src="https://images.unsplash.com/photo-1581091226033-d5c48150dbaa?w=600&h=400&fit=crop"
                alt="Blood pressure measurement"
                style={{ width: '100%', borderRadius: '20px', boxShadow: '0 10px 25px rgba(0,0,0,0.1)' }}
              />
              <WarningBox>
                <WarningTitle>⚠️ Why It's Called "The Silent Killer"</WarningTitle>
                <p style={{ color: '#92400e', margin: 0 }}>
                  High blood pressure often has NO symptoms. Many people have it for years without knowing, while it silently damages their heart, kidneys, and brain.
                </p>
              </WarningBox>
            </div>
          </TwoColumnGrid>
        </Section>

        {/* Blood Pressure Categories */}
        <Section delay="0.15s">
          <SectionTitle>Blood Pressure Categories</SectionTitle>
          <ThreeColumnGrid>
            <InfoCard>
              <InfoCardTitle>🟢 Normal</InfoCardTitle>
              <div style={{ fontSize: '2rem', fontWeight: 'bold', color: '#22c55e', marginBottom: '0.5rem' }}>&lt; 120 / &lt; 80</div>
              <InfoCardText>Ideal blood pressure. Maintain with healthy lifestyle habits.</InfoCardText>
            </InfoCard>
            <InfoCard>
              <InfoCardTitle>🟡 Elevated</InfoCardTitle>
              <div style={{ fontSize: '2rem', fontWeight: 'bold', color: '#eab308', marginBottom: '0.5rem' }}>120-129 / &lt; 80</div>
              <InfoCardText>Prehypertension. Lifestyle changes needed to prevent progression.</InfoCardText>
            </InfoCard>
            <InfoCard>
              <InfoCardTitle>🟠 Stage 1 Hypertension</InfoCardTitle>
              <div style={{ fontSize: '1.8rem', fontWeight: 'bold', color: '#f97316', marginBottom: '0.5rem' }}>130-139 / 80-89</div>
              <InfoCardText>Medical evaluation and lifestyle modifications recommended.</InfoCardText>
            </InfoCard>
            <InfoCard>
              <InfoCardTitle>🔴 Stage 2 Hypertension</InfoCardTitle>
              <div style={{ fontSize: '1.8rem', fontWeight: 'bold', color: '#dc2626', marginBottom: '0.5rem' }}>≥ 140 / ≥ 90</div>
              <InfoCardText>Medication usually required along with lifestyle changes.</InfoCardText>
            </InfoCard>
            <InfoCard>
              <InfoCardTitle>⚫ Hypertensive Crisis</InfoCardTitle>
              <div style={{ fontSize: '1.8rem', fontWeight: 'bold', color: '#7f1d1d', marginBottom: '0.5rem' }}>&gt; 180 / &gt; 120</div>
              <InfoCardText>Emergency! Seek immediate medical attention.</InfoCardText>
            </InfoCard>
          </ThreeColumnGrid>
        </Section>

        {/* Causes and Risk Factors */}
        <Section delay="0.2s">
          <SectionTitle>Causes & Risk Factors</SectionTitle>
          <TwoColumnGrid>
            <InfoCard>
              <InfoCardTitle>📋 Modifiable Risk Factors</InfoCardTitle>
              <ul style={{ listStyle: 'none', paddingLeft: 0 }}>
                <ListItem>High sodium diet (more than 2300mg/day)</ListItem>
                <ListItem>Physical inactivity and sedentary lifestyle</ListItem>
                <ListItem>Obesity and excess body weight</ListItem>
                <ListItem>Excessive alcohol consumption</ListItem>
                <ListItem>Tobacco use and smoking</ListItem>
                <ListItem>Chronic stress and poor sleep</ListItem>
                <ListItem>Uncontrolled diabetes</ListItem>
                <ListItem>High cholesterol</ListItem>
              </ul>
            </InfoCard>
            <InfoCard>
              <InfoCardTitle>🧬 Non-Modifiable Risk Factors</InfoCardTitle>
              <ul style={{ listStyle: 'none', paddingLeft: 0 }}>
                <ListItem>Age (risk increases with age)</ListItem>
                <ListItem>Family history of hypertension</ListItem>
                <ListItem>Gender (men more likely before 55, women after menopause)</ListItem>
                <ListItem>Race (higher prevalence in African Americans)</ListItem>
                <ListItem>Genetics and inherited conditions</ListItem>
              </ul>
              <SuccessBox style={{ marginTop: '1rem' }}>
                <WarningTitle>✅ Did You Know?</WarningTitle>
                <p style={{ color: '#166534', margin: 0 }}>
                  Even with genetic risk factors, lifestyle changes can reduce your risk of developing hypertension by up to 50%!
                </p>
              </SuccessBox>
            </InfoCard>
          </TwoColumnGrid>
        </Section>

        {/* Diabetes, Blood Pressure, and Heart Disease Connection */}
        <Section delay="0.25s">
          <SectionTitle>The Deadly Trio: Diabetes, High BP & Heart Disease</SectionTitle>
          <SectionSubtitle>
            These three conditions are closely connected and together significantly increase health risks
          </SectionSubtitle>
          
          <InfoCard>
            <InfoCardTitle>🔄 The Dangerous Connection</InfoCardTitle>
            <InfoCardText>
              <strong>How they interact:</strong> Diabetes damages blood vessels and nerves. High blood pressure puts additional stress on already damaged vessels. Together, they dramatically accelerate heart disease, kidney failure, and stroke.
            </InfoCardText>
            <div style={{ 
              background: '#e0f2fe', 
              padding: '1rem', 
              borderRadius: '15px', 
              textAlign: 'center',
              marginTop: '1rem',
              marginBottom: '1rem'
            }}>
              <p style={{ margin: 0, fontWeight: 'bold', color: '#0c4a6e' }}>
                Diabetes → Damaged Blood Vessels → High Blood Pressure → Heart Disease → Stroke/Kidney Failure
              </p>
            </div>
            <InfoCardText>
              <strong>Statistics:</strong> 2 out of 3 people with diabetes also have high blood pressure. People with both conditions have 4x higher risk of heart attack and stroke.
            </InfoCardText>
          </InfoCard>

          <TwoColumnGrid style={{ marginTop: '1.5rem' }}>
            <DangerBox>
              <WarningTitle color="#7f1d1d">🚨 Risks of Uncontrolled High BP</WarningTitle>
              <ul style={{ listStyle: 'none', paddingLeft: 0, margin: 0 }}>
                <ListItem style={{ color: '#7f1d1d' }}>Heart attack and heart failure</ListItem>
                <ListItem style={{ color: '#7f1d1d' }}>Stroke (hemorrhagic and ischemic)</ListItem>
                <ListItem style={{ color: '#7f1d1d' }}>Kidney disease and kidney failure</ListItem>
                <ListItem style={{ color: '#7f1d1d' }}>Vision loss (hypertensive retinopathy)</ListItem>
                <ListItem style={{ color: '#7f1d1d' }}>Peripheral artery disease</ListItem>
                <ListItem style={{ color: '#7f1d1d' }}>Cognitive decline and dementia</ListItem>
                <ListItem style={{ color: '#7f1d1d' }}>Sexual dysfunction</ListItem>
              </ul>
            </DangerBox>
            <SuccessBox>
              <WarningTitle color="#166534">✅ Benefits of Controlled BP</WarningTitle>
              <ul style={{ listStyle: 'none', paddingLeft: 0, margin: 0 }}>
                <ListItem style={{ color: '#166534' }}>40% lower risk of stroke</ListItem>
                <ListItem style={{ color: '#166534' }}>25% lower risk of heart attack</ListItem>
                <ListItem style={{ color: '#166534' }}>50% lower risk of heart failure</ListItem>
                <ListItem style={{ color: '#166534' }}>Protects kidney function</ListItem>
                <ListItem style={{ color: '#166534' }}>Preserves vision and cognitive function</ListItem>
                <ListItem style={{ color: '#166534' }}>Improves overall quality of life</ListItem>
              </ul>
            </SuccessBox>
          </TwoColumnGrid>
        </Section>

        {/* Management and Treatment */}
        <Section delay="0.3s">
          <SectionTitle>Managing Your Blood Pressure</SectionTitle>
          <SectionSubtitle>
            Evidence-based strategies for blood pressure control
          </SectionSubtitle>
          
          <InfoCard>
            <InfoCardTitle>🥗 Lifestyle Modifications (First Line of Defense)</InfoCardTitle>
            <ThreeColumnGrid>
              <div>
                <RiskBadge color="#0284c7">1</RiskBadge>
                <strong style={{ color: '#1e293b' }}>DASH Diet</strong>
                <ul style={{ listStyle: 'none', paddingLeft: 0, marginTop: '0.5rem' }}>
                  <ListItem>High in fruits, vegetables, whole grains</ListItem>
                  <ListItem>Low-fat dairy, lean proteins</ListItem>
                  <ListItem>Limit sodium to &lt; 1500-2300mg/day</ListItem>
                  <ListItem>Reduce saturated fats and added sugars</ListItem>
                </ul>
              </div>
              <div>
                <RiskBadge color="#0284c7">2</RiskBadge>
                <strong style={{ color: '#1e293b' }}>Regular Exercise</strong>
                <ul style={{ listStyle: 'none', paddingLeft: 0, marginTop: '0.5rem' }}>
                  <ListItem>150 minutes moderate aerobic exercise/week</ListItem>
                  <ListItem>Add strength training 2-3x/week</ListItem>
                  <ListItem>Walk, swim, cycle, or dance</ListItem>
                  <ListItem>Reduce sedentary time</ListItem>
                </ul>
              </div>
              <div>
                <RiskBadge color="#0284c7">3</RiskBadge>
                <strong style={{ color: '#1e293b' }}>Weight Management</strong>
                <ul style={{ listStyle: 'none', paddingLeft: 0, marginTop: '0.5rem' }}>
                  <ListItem>Lose weight if overweight (BMI &gt; 25)</ListItem>
                  <ListItem>Each 1kg loss reduces BP by ~1 mmHg</ListItem>
                  <ListItem>Aim for waist circumference &lt; 40" (men), &lt; 35" (women)</ListItem>
                </ul>
              </div>
            </ThreeColumnGrid>
          </InfoCard>

          <InfoCard style={{ marginTop: '1.5rem' }}>
            <InfoCardTitle>💊 Medications</InfoCardTitle>
            <InfoCardText>
              When lifestyle changes aren't enough, your doctor may prescribe medications. Common classes include:
            </InfoCardText>
            <FourColumnGrid style={{ marginTop: '1rem' }}>
              <div>
                <RiskBadge color="#0284c7">ACE Inhibitors</RiskBadge>
                <p style={{ fontSize: '0.85rem', marginTop: '0.5rem', color: '#475569' }}>lisinopril, enalapril</p>
              </div>
              <div>
                <RiskBadge color="#0284c7">ARBs</RiskBadge>
                <p style={{ fontSize: '0.85rem', marginTop: '0.5rem', color: '#475569' }}>losartan, valsartan</p>
              </div>
              <div>
                <RiskBadge color="#0284c7">Calcium Blockers</RiskBadge>
                <p style={{ fontSize: '0.85rem', marginTop: '0.5rem', color: '#475569' }}>amlodipine, nifedipine</p>
              </div>
              <div>
                <RiskBadge color="#0284c7">Diuretics</RiskBadge>
                <p style={{ fontSize: '0.85rem', marginTop: '0.5rem', color: '#475569' }}>HCTZ, chlorthalidone</p>
              </div>
            </FourColumnGrid>
            <WarningBox style={{ marginTop: '1rem' }}>
              <WarningTitle>⚠️ Important</WarningTitle>
              <p style={{ color: '#92400e', margin: 0 }}>
                Never stop blood pressure medications without doctor supervision. Sudden withdrawal can cause dangerous spikes in blood pressure.
              </p>
            </WarningBox>
          </InfoCard>
        </Section>

        {/* Home Monitoring Guide */}
        <Section delay="0.35s">
          <SectionTitle>How to Monitor at Home</SectionTitle>
          <TwoColumnGrid>
            <InfoCard>
              <InfoCardTitle>📏 Proper Measurement Technique</InfoCardTitle>
              <ul style={{ listStyle: 'none', paddingLeft: 0 }}>
                <ListItem>Don't smoke, exercise, or drink caffeine 30 minutes before</ListItem>
                <ListItem>Sit quietly for 5 minutes before measuring</ListItem>
                <ListItem>Support your back and keep feet flat on floor</ListItem>
                <ListItem>Place cuff on bare arm at heart level</ListItem>
                <ListItem>Take 2-3 readings, 1 minute apart</ListItem>
                <ListItem>Measure at same time daily (morning and evening)</ListItem>
              </ul>
            </InfoCard>
            <InfoCard>
              <InfoCardTitle>📊 Tracking Your Numbers</InfoCardTitle>
              <InfoCardText>
                Keep a blood pressure log to share with your doctor. Record:
              </InfoCardText>
              <ul style={{ listStyle: 'none', paddingLeft: 0 }}>
                <ListItem>Date and time of measurement</ListItem>
                <ListItem>Systolic and diastolic readings</ListItem>
                <ListItem>Heart rate (pulse)</ListItem>
                <ListItem>Any medications taken</ListItem>
                <ListItem>Notes about diet, exercise, or stress</ListItem>
              </ul>
              <SuccessBox>
                <WarningTitle color="#166534">💡 Tip</WarningTitle>
                <p style={{ color: '#166534', margin: 0 }}>
                  Bring your home monitor to your doctor's appointment to ensure it's calibrated correctly.
                </p>
              </SuccessBox>
            </InfoCard>
          </TwoColumnGrid>
        </Section>

        {/* Dr. Raj Kamal Section */}
  {/*       <Section delay="0.4s">
          <DoctorSection>
            <DoctorGrid>
              <DoctorImage 
                src="https://images.unsplash.com/photo-1612349317150-e413f6a5b16d?w=300&h=300&fit=crop"
                alt="Dr. Raj Kamal"
              />
              <DoctorContent>
                <DoctorName>Dr. Raj Kamal, MD</DoctorName>
                <DoctorTitle>Senior Cardiologist & Hypertension Specialist</DoctorTitle>
                <DoctorDescription>
                  With over 16 years of specialized experience in managing hypertension and its complications, Dr. Raj Kamal has helped thousands of patients achieve optimal blood pressure control and prevent heart disease, stroke, and kidney failure.
                </DoctorDescription>
                <DoctorDescription>
                  <strong>How Dr. Raj Kamal can help you manage your blood pressure:</strong>
                </DoctorDescription>
                <ul style={{ listStyle: 'none', paddingLeft: 0, marginBottom: '1rem' }}>
                  <ListItem style={{ color: '#e0f2fe' }}>Comprehensive cardiovascular risk assessment</ListItem>
                  <ListItem style={{ color: '#e0f2fe' }}>24-hour ambulatory blood pressure monitoring</ListItem>
                  <ListItem style={{ color: '#e0f2fe' }}>Personalized medication plans with minimal side effects</ListItem>
                  <ListItem style={{ color: '#e0f2fe' }}>Lifestyle modification programs (DASH diet, exercise plans)</ListItem>
                  <ListItem style={{ color: '#e0f2fe' }}>Management of resistant hypertension</ListItem>
                  <ListItem style={{ color: '#e0f2fe' }}>Co-management of diabetes, high BP, and heart disease</ListItem>
                  <ListItem style={{ color: '#e0f2fe' }}>Regular follow-ups and home monitoring guidance</ListItem>
                  <ListItem style={{ color: '#e0f2fe' }}>Telemedicine consultations for convenient care</ListItem>
                </ul>
                <CTAButton onClick={handleAppointment}>Schedule BP Consultation →</CTAButton>
              </DoctorContent>
            </DoctorGrid>
          </DoctorSection>
        </Section> */}

        {/* Heart-Healthy Lifestyle Tips */}
        <Section delay="0.45s">
          <SectionTitle>Quick Tips for Healthy Blood Pressure</SectionTitle>
          <FourColumnGrid>
            <InfoCard>
              <InfoCardTitle>🧂 Reduce Sodium</InfoCardTitle>
              <InfoCardText>
                Use herbs and spices instead of salt. Read food labels. Choose low-sodium options.
              </InfoCardText>
            </InfoCard>
            <InfoCard>
              <InfoCardTitle>🍌 Eat More Potassium</InfoCardTitle>
              <InfoCardText>
                Bananas, sweet potatoes, spinach, avocados, and beans help balance sodium.
              </InfoCardText>
            </InfoCard>
            <InfoCard>
              <InfoCardTitle>🚶 Stay Active</InfoCardTitle>
              <InfoCardText>
                Even 10-minute walks throughout the day add up. Take stairs, park farther away.
              </InfoCardText>
            </InfoCard>
            <InfoCard>
              <InfoCardTitle>😴 Manage Stress</InfoCardTitle>
              <InfoCardText>
                Deep breathing, meditation, yoga, or talking to a friend can lower BP.
              </InfoCardText>
            </InfoCard>
          </FourColumnGrid>
        </Section>

        {/* Image Gallery */}
   {/*      <Section delay="0.5s">
          <SectionTitle>Hypertension Care Facilities</SectionTitle>
          <ImageGallery>
            {galleryImages.map((img, index) => (
              <GalleryImage key={index} src={img} alt={`Medical facility ${index + 1}`} />
            ))}
          </ImageGallery>
        </Section> */}

        {/* Final Call to Action */}
        <Section delay="0.55s">
          <div style={{ 
            background: 'linear-gradient(135deg, #0284c7, #0c4a6e)', 
            borderRadius: '20px', 
            padding: '2rem', 
            textAlign: 'center',
            color: 'white'
          }}>
            <h3 style={{ fontSize: '1.5rem', marginBottom: '1rem' }}>Know Your Numbers. Control Your Future.</h3>
            <p style={{ marginBottom: '1.5rem', maxWidth: '600px', margin: '0 auto 1.5rem' }}>
              High blood pressure can be managed effectively. Early detection and proper treatment can add years to your life. Don't wait for symptoms to appear.
            </p>
            <HeroButton onClick={handleAppointment} style={{ background: 'white', color: '#0284c7' }}>
              Check Your Blood Pressure Today
            </HeroButton>
          </div>
        </Section>
      </ContentContainer>
    </PageContainer>
  );
};

export default BloodPressurePage;